/**
 * Profiles troubleshooting page: explain behavior profile capability resolution for a store.
 * Request: store + capability (single or "All"). Results: human-readable explain JSON.
 */

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { SingleValue } from 'react-select';

import { IStore } from '../../../../services/api/troubleshooting-service';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { Button } from '../../../../components/inputs/button/button';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import LinearLoader from '../../../../components/loaders/linear-loader/linear-loader';

import TroubleshootingStore from '../../troubleshooting-page-store';
import { PROFILE_CAPABILITY_OPTIONS, IProfileCapabilityOption } from './profile-capability-options';
import ProfileExplanationDisplay from './profile-explanation-display/profile-explanation-display';

import './profiles-page.scss';

const ProfilesPage: React.FC = observer(() => {
    const [chosenStore, setChosenStore] = useState<IOption | null>(null);
    const [chosenCapability, setChosenCapability] = useState<IOption | null>(null);
    const [shopifyResponse, setShopifyResponse] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;

    useEffect(() => {
        if (chosenStore?.value !== portalId && portalId && portalName) {
            setChosenStore({
                value: portalId,
                label: portalName + (portalDisplayName ? ' (' + portalDisplayName + ')' : '')
            });
        }
    }, [portalId, portalName, portalDisplayName, chosenStore?.value]);

    const __shopifyResponse = TroubleshootingStore.shopifyResponse;
    useEffect(() => {
        setShopifyResponse(toJS(TroubleshootingStore.shopifyResponse));
        setIsLoading(false);
        setErrorMessage(null);
    }, [__shopifyResponse]);

    const capabilitySelectOptions: IOption[] = PROFILE_CAPABILITY_OPTIONS.map((opt: IProfileCapabilityOption) => ({
        value: opt.value,
        label: opt.label
    }));

    const generateStoreSelectValues = (): IOption[] => {
        return TroubleshootingStore.stores?.map((el: IStore): IOption => ({
            value: el.id,
            label: el.name + (el.display_name ? ' (' + el.display_name + ')' : '')
        })) || [];
    };

    const onStoreChange = (val: SingleValue<IOption>) => {
        setChosenStore(val || null);
        setShopifyResponse(null);
        setErrorMessage(null);
    };

    const onCapabilityChange = (val: SingleValue<IOption>) => {
        setChosenCapability(val || null);
        setErrorMessage(null);
    };

    const performExplain = async () => {
        if (!chosenStore?.value) return;
        const capability = chosenCapability?.value ?? 'all';
        setErrorMessage(null);
        setIsLoading(true);
        try {
            await TroubleshootingStore.fetchProfileExplanations(chosenStore.value, `${capability}`);
        } catch (err: any) {
            setErrorMessage(err?.message || 'Request failed');
            TroubleshootingStore.resetShopifyResponse();
        } finally {
            setIsLoading(false);
        }
    };

    const canPerformExplain = !!chosenStore?.value;
    const hasData = shopifyResponse?.data != null;

    return (
        <div className="profiles-page">
            <div className="card">
                <h4>Request Information</h4>
                <div className="controls">
                    <p className="head5">Store</p>
                    <SelectDropdown
                        required
                        value={chosenStore || undefined}
                        placeholder="Type to find and select store"
                        options={generateStoreSelectValues()}
                        onChange={onStoreChange}
                    />
                </div>
                <div className="controls">
                    <p className="head5">Capability</p>
                    <SelectDropdown
                        value={chosenCapability || undefined}
                        placeholder="Select capability or All"
                        options={capabilitySelectOptions}
                        onChange={onCapabilityChange}
                    />
                </div>
                <Button
                    disabled={!canPerformExplain}
                    text="Explain"
                    onClick={performExplain}
                />
            </div>

            {isLoading && (
                <div className="card">
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        Loading profile explanations. Please wait...
                        <LinearLoader />
                    </FontBody2>
                </div>
            )}

            {errorMessage && (
                <div className="card results-card">
                    <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER} variant={FONT_VARIANT.BOLD}>
                        {errorMessage}
                    </FontBody2>
                </div>
            )}

            {!isLoading && hasData && !errorMessage && (
                <div className="card results-card">
                    <h4>Results</h4>
                    {shopifyResponse.data.explanations ? (
                        <div className="profiles-explanations-list">
                            {shopifyResponse.data.explanations.map((item: any, index: number) => (
                                <ProfileExplanationDisplay
                                    key={item?.capability?.key ?? index}
                                    data={item}
                                    defaultExpanded={index === 0}
                                />
                            ))}
                        </div>
                    ) : (
                        <ProfileExplanationDisplay
                            data={shopifyResponse.data}
                            defaultExpanded={true}
                        />
                    )}
                </div>
            )}
        </div>
    );
});

export default ProfilesPage;
